import * as tracking from 'dibs-tracking';
import SV from 'server-vars';
import { LOCALE_US } from 'dibs-intl/exports/locales';

const {
    trackingConstants: { ECOM_PRODUCT_CLICK, ECOM_PRODUCT_IMPRESSION },
    eventNameConstants: { EVENT_SELECT_ITEM, EVENT_VIEW_ITEM_LIST, EVENT_PRODUCT_INTERACTION },
    interactionTypeConstants: { INTERACTION_TYPE_PRODUCT_CAROUSEL },
    stepInteractionConstants: { STEP_INTERACTION_VIEW_ALL_CLICK },
} = tracking;

function getProductTrackingData(item, index) {
    const { browseUrl, categoryCode, ecommerceTrackingParams, title, isSponsored, serviceId } =
        item?.node || {};
    return {
        position: index + 1,
        category: browseUrl || '',
        categoryCode: categoryCode || '',
        ...(ecommerceTrackingParams || {}),
        id: ecommerceTrackingParams?.id || serviceId,
        name: SV.get('locale') === LOCALE_US ? title : 'n/a',
        ...(typeof isSponsored === 'boolean' ? { dimension141: isSponsored } : {}),
    };
}

function fireEcommerceImpressions(itemEdges, list, startingIndex = 0) {
    tracking.trackEcommerce({
        type: ECOM_PRODUCT_IMPRESSION,
        eventName: EVENT_VIEW_ITEM_LIST,
        products: itemEdges.map((itemEdge, index) => ({
            list,
            ...getProductTrackingData(itemEdge, startingIndex + index),
        })),
    });
}

function fireEcommerceClick(index, itemEdge, list) {
    tracking.trackEcommerce({
        type: ECOM_PRODUCT_CLICK,
        eventName: EVENT_SELECT_ITEM,
        products: [
            {
                ...getProductTrackingData(itemEdge, index),
            },
        ],
        actionField: { list, itemId: itemEdge?.node?.serviceId },
    });
}

/**
 * @see PdpRespSimilarItemsLanding.jsx
 */
const LANDING_ACTION = 'landing module clicked';
const LANDING_CATEGORY = 'landing module interaction';
const LANDING_LIST = 'pdp browse similar items';
const LANDING_LIST_SOLD = 'sold pdp browse similar items';

export function trackLandingItemImpressions(landingItems, isSoldPdp) {
    fireEcommerceImpressions(landingItems, isSoldPdp ? LANDING_LIST_SOLD : LANDING_LIST);
}

export function trackItemClick({ moduleType = '', actionValue = '' } = {}, event) {
    const label = `related items${moduleType && ' - ' + moduleType}`;
    const action = `${LANDING_ACTION}${actionValue && ' - ' + actionValue}`;
    tracking.trackEvent(
        {
            label,
            action,
            category: LANDING_CATEGORY,
        },
        event || null
    );
}

export function trackEcommerceLandingItemClick({ index, landingItem, isSoldPdp }) {
    fireEcommerceClick(index, landingItem, isSoldPdp ? LANDING_LIST_SOLD : LANDING_LIST);
}

export function trackLandingViewAllClick(event) {
    tracking.trackEvent(
        {
            label: 'related items view all',
            action: LANDING_ACTION,
            category: LANDING_CATEGORY,
        },
        event || null
    );
}

/**
 * @see PdpRespSimilarItemsGrid.jsx
 */
export const SIMILAR_ITEMS_LIST = 'pdp you may also like';
export const VISUALLY_SIMILAR_ITEMS_LIST = 'pdp visually similar';
export const PERSONALIZATION_MODULE_ITEMS_LIST = 'pdp personalization module';

export function trackSimilarItemImpressions(
    gridItems,
    location = SIMILAR_ITEMS_LIST,
    startingIndex = 0
) {
    const items = gridItems.map(item => ({ node: item }));
    fireEcommerceImpressions(items, location, startingIndex);
}

export function trackSimilarItemClick(
    index,
    gridItem,
    location = SIMILAR_ITEMS_LIST,
    trackingVariant
) {
    const item = { node: gridItem };
    fireEcommerceClick(index, item, location, trackingVariant);
}

const COMPLETE_THE_LOOK_LIST = 'pdp complete the look';
export function trackComplementaryItemClick(index, gridItem) {
    fireEcommerceClick(index, gridItem, COMPLETE_THE_LOOK_LIST);
}

export function trackComplementaryItemImpressions(gridItems) {
    fireEcommerceImpressions(gridItems, COMPLETE_THE_LOOK_LIST);
}

/**
 * @see PdpRespSellerItemsGrid.jsx
 */

export function trackSellerPageClick(event) {
    tracking.trackEvent(
        {
            action: 'click to seller page',
            category: 'dealer click',
            label: 'PDP',
            eventName: EVENT_PRODUCT_INTERACTION,
            interaction_type: INTERACTION_TYPE_PRODUCT_CAROUSEL,
            step_interaction_name: STEP_INTERACTION_VIEW_ALL_CLICK,
            trigger: 'more from this seller',
        },
        event || null
    );
}

export function trackItemsDisplayed({ moduleType = '', actionValue = '' } = {}) {
    const label = `related items${moduleType && ' - ' + moduleType}`;
    const action = `landing module displayed${actionValue && ' - ' + actionValue}`;
    tracking.trackEvent({
        action,
        label,
        category: LANDING_CATEGORY,
    });
}

export function trackViewMoreRelatedItemsClick(event) {
    tracking.trackEvent(
        {
            action: 'YMAL view button clicked',
            category: 'promo interaction',
            label: 'view more',
        },
        event || null
    );
}

export function trackViewLessRelatedItemsClick(event) {
    tracking.trackEvent(
        {
            action: 'YMAL view button clicked',
            category: 'promo interaction',
            label: 'view less',
        },
        event || null
    );
}

export function trackViewMoreVisuallySimilarClick(event) {
    tracking.trackEvent(
        {
            action: 'view more clicked',
            category: 'product',
            label: VISUALLY_SIMILAR_ITEMS_LIST,
        },
        event || null
    );
}

export const trackPersonalizedItemsVisible = () => {
    tracking.trackEvent({
        action: 'personalization module reached',
        category: 'product',
        label: 'pdp',
    });
};

export const trackPersonalizedItemsRendered = (totalItemsSize, totalPersonalizedItemsSize) => {
    tracking.trackEvent({
        action: 'personalization module present',
        category: 'product',
        label: `pdp - ${totalPersonalizedItemsSize}/${totalItemsSize} personalized`,
        isInteractiveEvent: false,
    });
};
